import { type Draft, produce } from 'immer';
import { create } from 'zustand';
import { devtools, type NamedSet } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';

import { openReplayStateLogger } from '../../../hooks/open-replay';
import type { ReportBuilderDeliverable } from './report-builder.model';

// ── State Types ──

type ReportBuilderReportsMap = Record<string, ReportBuilderDeliverable>;

export type ReportBuilderState = {
  reportIds: ReportBuilderDeliverable['id'][];
  reports: ReportBuilderReportsMap;
};

export type ReportBuilderActions = {
  addReport: (
    report: ReportBuilderDeliverable | ReportBuilderDeliverable[]
  ) => void;
  removeReports: (ids: ReportBuilderDeliverable['id'][]) => void;
  setReports: (reports: ReportBuilderDeliverable[]) => void;
  updateReport: (
    id: ReportBuilderDeliverable['id'],
    updater: (report: ReportBuilderDeliverable) => ReportBuilderDeliverable
  ) => void;
  reset: () => void;
};

export type ReportBuilderStore = ReportBuilderState & {
  actions: ReportBuilderActions;
};

// ── Helpers ──

const getReportKey = (id: ReportBuilderDeliverable['id']) => String(id);

const getReportFromMap = (
  reports: ReportBuilderReportsMap,
  id: ReportBuilderDeliverable['id']
) => reports[getReportKey(id)];

const addReportToState = (
  draft: Draft<ReportBuilderState>,
  report: ReportBuilderDeliverable
) => {
  const key = getReportKey(report.id);
  const exists = draft.reportIds.some((id) => String(id) === key);
  if (!exists) {
    draft.reportIds.push(report.id);
  }
  draft.reports[key] = report;
};

// ── Initial State ──

const createInitialState = (): ReportBuilderState => ({
  reportIds: [],
  reports: {},
});

// ── Actions ──

const createReportBuilderActions = (
  set: NamedSet<ReportBuilderStore>
): ReportBuilderActions => ({
  addReport: (payload) =>
    set(
      (state) =>
        produce(state, (draft) => {
          const items = Array.isArray(payload) ? payload : [payload];
          // biome-ignore lint/suspicious/useIterableCallbackReturn: migrated from develop
          items.forEach((report) => addReportToState(draft, report));
        }),
      false,
      'addReport'
    ),
  removeReports: (ids) =>
    set(
      (state) =>
        produce(state, (draft) => {
          const keysToRemove = new Set(ids.map((id) => String(id)));
          draft.reportIds = draft.reportIds.filter(
            (id) => !keysToRemove.has(String(id))
          );
          keysToRemove.forEach((key) => {
            delete draft.reports[key];
          });
        }),
      false,
      'removeReports'
    ),
  setReports: (reports) =>
    set(
      (state) =>
        produce(state, (draft) => {
          draft.reportIds = reports.map((r) => r.id);
          draft.reports = {};
          reports.forEach((report) => {
            draft.reports[getReportKey(report.id)] = report;
          });
        }),
      false,
      'setReports'
    ),
  updateReport: (id, updater) =>
    set(
      (state) =>
        produce(state, (draft) => {
          const key = getReportKey(id);
          const existing = draft.reports[key];
          if (existing) {
            draft.reports[key] = updater(existing as ReportBuilderDeliverable);
          }
        }),
      false,
      'updateReport'
    ),
  reset: () =>
    set(
      (state) => ({
        ...createInitialState(),
        actions: state.actions,
      }),
      false,
      'reset'
    ),
});

// ── Store ──

const useReportBuilderStore = create<ReportBuilderStore>()(
  devtools(
    openReplayStateLogger(
      (set) => ({
        ...createInitialState(),
        actions: createReportBuilderActions(set),
      }),
      'report_builder'
    ),
    { name: 'ReportBuilderStore' }
  )
);

// ── Store Accessor (for non-React code) ──

export const reportBuilderStore = {
  getState: useReportBuilderStore.getState,
  setState: useReportBuilderStore.setState,
  subscribe: useReportBuilderStore.subscribe,
  get actions() {
    return useReportBuilderStore.getState().actions;
  },
};

// ── Hooks ──

export const useReportBuilderSelector = <T>(
  selector: (state: ReportBuilderState) => T,
  equalityFn?: (a: T, b: T) => boolean
) => useStoreWithEqualityFn(useReportBuilderStore, selector, equalityFn);
// ── Selectors ──

export const selectAllReports = (
  state: ReportBuilderState
): ReportBuilderDeliverable[] =>
  state.reportIds
    .map((id) => getReportFromMap(state.reports, id))
    .filter((v): v is ReportBuilderDeliverable => !!v);

/** Reactive hook with shallow equality — avoids re-renders when array contents haven't changed */
export const useAllReports = () =>
  useStoreWithEqualityFn(useReportBuilderStore, selectAllReports, shallow);

export const selectReport =
  (id: ReportBuilderDeliverable['id']) => (state: ReportBuilderState) =>
    getReportFromMap(state.reports, id);

// ── Sync Snapshot Helpers ──
export const getReport = (id: ReportBuilderDeliverable['id']) =>
  selectReport(id)(reportBuilderStore.getState());
